import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { AudioProgressEv, ExternalAudioPlayer, Media, MediaEvent, MediaEventType, MediaState, MediaType } from "@motes-sdk/models";
import { filter } from "rxjs";
import { AutoSub, AutoSubOnDestroy } from "../../providers";
import { MotesMediaPlayerService } from "../media-player.service";

@AutoSubOnDestroy()
@Component({
    selector: 'audio-player',
    templateUrl: 'audio-player.component.html',
    styleUrls: ['./audio-player.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Default,
})
export class AudioPlayer implements OnInit {

    @ViewChild('audio') audio: ElementRef<HTMLAudioElement>;

    public media: Media;
    public externalAudioPlayer: ExternalAudioPlayer = null;
    public loadingPct: number = 0;
    public duration: number = 0;
    private bufferPct: number = 0;

    public MIN_LOAD_SEC = 15;

    // Expose to view
    public MediaType = MediaType;

    constructor(
        private mediaPlayerService: MotesMediaPlayerService,
        private cd: ChangeDetectorRef) {}

    ngOnInit(): void {
        AutoSub(this).reg['mediaEv'] = this.mediaPlayerService.watchMediaEvents().pipe(filter((ev) => this.isSound(ev))).subscribe((event: MediaEvent) => {

            this.media = (event.state == MediaState.NONE) ? null : event.media;

            if(event.type == MediaEventType.STATEUPDATE) this.onMediaEvent(event);
            else if(event.type == MediaEventType.SEEK) this.handleSeekEvent(event, event.seekSec, event.duration, event.progress);
            else if(event.type == MediaEventType.SET_CURRENT_TIME) this.handleSetTimeEvent(event, event.seekSec);
        })

        AutoSub(this).reg['externalPlayerSub'] = this.mediaPlayerService.watchExternalMediaPlayer().subscribe((externalAudioPlayer) => {
            this.externalAudioPlayer = externalAudioPlayer;
        })
    }

    private isSound(ev: MediaEvent): boolean {
        return ev && ev.media && (ev.media.type == MediaType.SOUND || ev.media.type == MediaType.AMBISONIC);
    }

    public async onMediaEvent(ev: MediaEvent) {

        if(ev.state == MediaState.PLAYING) {

            if(this.externalAudioPlayer) {
                this.bufferPct = 100;
                this.externalAudioPlayer.play(ev.media?.key, ev.media?.sound?.url);
            } else {
                // Wait for audio element to exist
                await new Promise((resolve) => setTimeout(resolve, 100))
                if(this.audio && this.audio.nativeElement) this.audio.nativeElement.play();
            }
        }
        else if(ev.state == MediaState.PAUSED) {
            if(this.externalAudioPlayer) this.externalAudioPlayer.pause(ev.media?.key);
            else if(this.audio && this.audio.nativeElement) this.audio.nativeElement.pause();
        }
        else if(ev.state == MediaState.NONE) {
            if(this.externalAudioPlayer) this.externalAudioPlayer.stop(ev.media?.key);

            // Reset state
            this.duration = 0;
            this.bufferPct = 0;
        }
    }

    private handleSeekEvent(ev: MediaEvent, seekSec: number, duration: number, progress: number) {
        if(!ev.media) {
            console.error("Got media event without media, skip seeking", ev);
            return;
        }

        if(this.externalAudioPlayer) {
            const percentPlayed = Math.round(progress * 10) / 10;
            const currentTime = (percentPlayed / 100) * duration;
            const newTime = Math.min(currentTime + seekSec, duration - 2);
            this.externalAudioPlayer.setTime(ev.media.key, newTime);
        } else {
            if(!this.audio) return;
            let audioEl = this.audio.nativeElement;
            audioEl.currentTime = audioEl.currentTime + seekSec;
        }
    }

    private handleSetTimeEvent(ev: MediaEvent, seekSec: number) {
        if(!ev.media) {
            console.error("Got media event without media, skip set time", ev);
            return;
        }

        if(this.externalAudioPlayer) {
            this.externalAudioPlayer.setTime(ev.media.key, seekSec);
        } else {
            if(!this.audio) return;
            this.audio.nativeElement.currentTime = seekSec;
        }
    }

    public mediaEnded(): void {
        this.mediaPlayerService.mediaEnded();
    }

    /**
    * Time update callback from audio player
    */
     public timeUpdate(generalEv: AudioProgressEv | Event) {
        // Obs, it seems angular is returning a wrong type from the dom so we need to typecast to AudioProgressEv
        let ev = generalEv as any as AudioProgressEv;
        let audio = ev.srcElement;

        if(!audio) return;
        if(!audio.duration) return;

        // Set duration if the whole file was loaded 
        if(isFinite(audio.duration)) this.duration = audio.duration;

        let percentPlayed: number = (isFinite(this.duration)) ? (audio.currentTime / audio.duration) * 100 : 0;

        this.mediaPlayerService.updateProgress(percentPlayed, audio.currentTime, this.duration);
        this.cd.markForCheck();
    }

    /**
     * Audio player canplay event handler
     */
     public canplayUpdate(ev: AudioProgressEv | Event) {
        // In some rare cases the progress update ev comes before
        // the buffer time range array is > 0, for this reason
        // we need to update buffering on canplay as well
        this.updateBuffering(ev as any as AudioProgressEv);
    }

    /**
     * Progress (buffer events) from audio player
     */
     public progressUpdate(ev: AudioProgressEv | ProgressEvent<EventTarget>) {
        this.updateBuffering(ev as any as AudioProgressEv);
    }

    private updateBuffering(ev: AudioProgressEv) {
        let audio = ev.srcElement;
        if(!audio) return;
        if(!audio.buffered || audio.buffered.length <= 0) return;

        if(audio.duration < 0) return;

        // Okay we got at duration, buffering has begun
        this.duration = audio.duration;

        // Get number of time ranges
        let bufferSize = audio.buffered.length - 1;

        // Loop through all auio audio buffer time ranges
        for(let i = 0; i < audio.buffered.length; i++) {
            let lastStart = audio.buffered.start(bufferSize - i);
            let lastEnd = audio.buffered.end(bufferSize - i);

            // Calc buffer pct from the last range starting below current time
            if(lastStart <= audio.currentTime) {
                this.bufferPct = Math.round((lastEnd / this.duration) * 100);
                this.loadingPct = Math.round((lastEnd - lastStart) / this.MIN_LOAD_SEC * 100);
                break;
            }
        }
        this.mediaPlayerService.updateLoadProgress(this.loadingPct, this.duration, this.bufferPct);

        let status = (this.loadingPct >= 100) ? "" : "Loading... ";
        this.mediaPlayerService.setLoadingStatus(status);
        this.cd.markForCheck();
    }
}